// The market's own arithmetic, in the one place both a page and a chart can
// reach it.
//
// There is exactly one thing here so far — the spread — and it is here rather
// than in pages/itemQuote.ts because two things now compute it: the item page's
// headline figure and the price chart's per-day tooltip. Left in the page
// module, the chart would either import from a page (backwards) or subtract the
// two numbers itself (a second copy of a convention that inverts if read the
// other way). Neither is acceptable for a sign that flips the meaning of the
// figure it is attached to.

/** The gap between the best bid and the best ask, and what fraction of the ask
 *  it is. */
export interface Spread {
  /** Adena. Negative on an ordinary market; see `spreadOf`. */
  gap: number;
  /** Per cent of the ask. Null when the ask is zero. */
  pct: number | null;
}

/** `bid - ask`, and that sense is not a choice made here.
 *
 *  `server/lib/queries.py` opens by defining spread as buy minus sell,
 *  "positive when someone pays more than someone asks", and `best_prices`
 *  ships a column computed that way. A second convention on the one page that
 *  shows both figures at once would be the worst possible place to have two.
 *
 *  So this is normally **negative**: you buy at the ask and sell at the bid, and
 *  an instant round trip loses the difference. A positive spread is a crossed
 *  market — money on the table right now — and it is the state a trader opens
 *  this page hoping to find. The sign is what tells the two apart, which is why
 *  callers print it through `signed()` rather than as an absolute width.
 *
 *  The percentage is against the **ask**, because the ask is what entering the
 *  round trip costs: "−18%" reads as eighteen per cent of the outlay. Null
 *  rather than Infinity when the ask is zero — a giveaway at 0 adena is a thing
 *  traders do, and no percentage of nothing is worth printing. The gap in that
 *  case is still a real number of adena, so the two fields degrade separately.
 *
 *  Both arguments are required. There is deliberately no "treat a missing side
 *  as zero" path: that would print a spread the size of the other side and call
 *  it a market. A caller with one side missing has no spread, and says so. */
export function spreadOf(bid: number, ask: number): Spread {
  const gap = bid - ask;
  return { gap, pct: ask === 0 ? null : (gap / ask) * 100 };
}
